// Best-effort protection for unsaved console input when the admin session
// expires: text fields on the current page are kept in this tab's
// sessionStorage while the administrator signs in again, then filled back in.
// Passwords, hidden and file inputs are never stored.

export type DraftField = { key: string; value: string };
type DraftSnapshot = { path: string; savedAt: number; fields: DraftField[] };
type FieldLike = {
  tagName: string;
  type?: string;
  name?: string;
  id?: string;
  value: string;
  readOnly?: boolean;
  getAttribute(name: string): string | null;
  closest?(selector: string): { textContent: string | null } | null;
  dispatchEvent?(event: Event): boolean;
};
type RootLike = { querySelectorAll(selector: string): ArrayLike<unknown> };

const storageKey = 'realrain:admin:session-drafts';
const draftMaxAge = 30 * 60 * 1000;
const textInputTypes = new Set(['', 'text', 'search', 'email', 'url', 'tel', 'number', 'date', 'time', 'datetime-local']);

/** A stable name for a field: its name, id, accessible label, placeholder or wrapping label text. */
export function draftFieldName(element: FieldLike): string | undefined {
  const tag = element.tagName.toLowerCase();
  if (tag !== 'textarea' && (tag !== 'input' || !textInputTypes.has((element.type ?? '').toLowerCase()))) return undefined;
  const label = element.name || element.id || element.getAttribute('aria-label') || element.getAttribute('placeholder')
    || element.closest?.('label')?.textContent?.trim();
  return label ? `${tag}:${label}` : undefined;
}

function fields(root: RootLike): Array<{ element: FieldLike; key: string }> {
  const counts = new Map<string, number>();
  const result: Array<{ element: FieldLike; key: string }> = [];
  for (const candidate of Array.from(root.querySelectorAll('input, textarea')) as FieldLike[]) {
    const name = draftFieldName(candidate);
    if (!name) continue;
    const index = counts.get(name) ?? 0;
    counts.set(name, index + 1);
    result.push({ element: candidate, key: `${name}#${index}` });
  }
  return result;
}

export function collectDrafts(root: RootLike): DraftField[] {
  return fields(root)
    .filter(({ element }) => !element.readOnly && element.value.trim() !== '')
    .map(({ element, key }) => ({ key, value: element.value }));
}

export function saveDrafts(storage: Pick<Storage, 'setItem'>, path: string, drafts: DraftField[], now = Date.now()): void {
  if (!drafts.length) return;
  try {
    storage.setItem(storageKey, JSON.stringify({ path, savedAt: now, fields: drafts } satisfies DraftSnapshot));
  } catch {
    // Storage can be full or blocked; the overlay still lets the admin sign in again.
  }
}

/** Returns and forgets drafts saved for this page; old or foreign drafts are dropped. */
export function takeDrafts(storage: Pick<Storage, 'getItem' | 'removeItem'>, path: string, now = Date.now()): DraftField[] {
  let snapshot: DraftSnapshot | undefined;
  try {
    const raw = storage.getItem(storageKey);
    snapshot = raw ? JSON.parse(raw) as DraftSnapshot : undefined;
  } catch {
    snapshot = undefined;
  }
  if (!snapshot || snapshot.path !== path) return [];
  try { storage.removeItem(storageKey); } catch { /* Storage is optional. */ }
  if (now - snapshot.savedAt > draftMaxAge || !Array.isArray(snapshot.fields)) return [];
  return snapshot.fields.filter(field => typeof field?.key === 'string' && typeof field.value === 'string');
}

/**
 * Fills empty fields with their saved values. React-controlled inputs need the
 * native value setter plus an input event so their state updates too.
 */
export function restoreDrafts(root: RootLike, drafts: DraftField[]): number {
  if (!drafts.length) return 0;
  const saved = new Map(drafts.map(field => [field.key, field.value]));
  let restored = 0;
  for (const { element, key } of fields(root)) {
    const value = saved.get(key);
    if (value === undefined || element.value !== '') continue;
    const setter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), 'value')?.set;
    if (setter) setter.call(element, value); else element.value = value;
    element.dispatchEvent?.(new Event('input', { bubbles: true }));
    restored++;
  }
  return restored;
}

export const draftStorageKey = storageKey;
